import { useQuery, useMutation } from '@tanstack/react-query';
import { reportsApi } from '../../api/endpoints/reports';
import {
  ReportQueryParams,
  DailyCollectionsQueryParams,
  CustomerStatementQueryParams,
  ReportExportRequest,
} from '../../api/types/report.types';

// Dashboard Summary Report
export const useDashboardReport = () => {
  return useQuery({
    queryKey: ['dashboardReport'],
    queryFn: () => reportsApi.getDashboard(),
    staleTime: 1000 * 60 * 5, // 5 minutes (backend cache)
  });
};

// Portfolio Analysis Report
export const usePortfolioAnalysis = (params: ReportQueryParams) => {
  return useQuery({
    queryKey: ['portfolioAnalysis', params],
    queryFn: () => reportsApi.getPortfolioAnalysis(params),
    enabled: !!params.startDate && !!params.endDate,
    staleTime: 1000 * 60 * 15, // 15 minutes (backend cache)
  });
};

// Loan Performance Report
export const useLoanPerformance = (params: ReportQueryParams) => {
  return useQuery({
    queryKey: ['loanPerformance', params],
    queryFn: () => reportsApi.getLoanPerformance(params),
    enabled: !!params.startDate && !!params.endDate,
  });
};

// Customer Statement
export const useCustomerStatement = (params: CustomerStatementQueryParams) => {
  return useQuery({
    queryKey: ['customerStatement', params],
    queryFn: () => reportsApi.getCustomerStatement(params),
    enabled: !!params.customerId && !!params.startDate && !!params.endDate,
  });
};

// Daily Collections Report
export const useDailyCollections = (params: DailyCollectionsQueryParams) => {
  return useQuery({
    queryKey: ['dailyCollections', params],
    queryFn: () => reportsApi.getDailyCollections(params),
    enabled: !!params.date,
  });
};

// Export Report Mutation
export const useExportReport = () => {
  return useMutation({
    mutationFn: async (data: ReportExportRequest) => {
      const blob = await reportsApi.exportReport(data);

      // Get filename based on report type and format
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${data.reportType}-${timestamp}.${data.format}`;

      // Download file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    },
  });
};
